import { useErrorStore } from '@/stores/error'
import { useSuccessStore } from '@/stores/success'
import { DocumentStatuses } from '@/types/documents'
import type { Document } from '@/types/documents'
import type { Me } from '@/types/me'
import { checkTimeInHourRange, isOnWeekday, isWeekdayInRange } from '@/utils/date'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

export enum AdviserStatus {
  AVAILABLE,
  BACK_AT_TIME,
  UNAVAILABLE,
  UNKNOWN
}

const BANNER_DURATION = 3000

/**
 * Determine the adviser status based on the current time and business rules
 * AVAILABLE VERSION should be displayed on weekdays from 09:30 à 12:45 and from 14:00 to 18:00
 * BACK AT 14H00 VERSION should be displayed on weekdays between 12:46 and 13:59
 * WEEKEND AND EVENING VERSION should be displayed on weekdays from 18:01 to 9:29 AND during the weekends
 */
export const getAdviserStatus = () =>
  computed(() => {
    const currentDate = new Date()
    const availableRange: [Date, Date] = [new Date(0, 0, 0, 9, 30), new Date(0, 0, 0, 12, 45)]
    const availableRangeTwo: [Date, Date] = [new Date(0, 0, 0, 14, 0), new Date(0, 0, 0, 18, 0)]

    const backAtTimeRange: [Date, Date] = [new Date(0, 0, 0, 12, 46), new Date(0, 0, 0, 13, 59)]
    const unavailableRange: [Date, Date] = [new Date(0, 0, 0, 18, 1), new Date(0, 0, 0, 9, 29)]

    if (isWeekdayInRange(currentDate, availableRange, availableRangeTwo)) return AdviserStatus.AVAILABLE

    if (isWeekdayInRange(currentDate, backAtTimeRange)) return AdviserStatus.BACK_AT_TIME

    if (
      isOnWeekday(currentDate) ||
      checkTimeInHourRange(currentDate.getHours() * 100 + currentDate.getMinutes(), unavailableRange)
    )
      return AdviserStatus.UNAVAILABLE

    return AdviserStatus.UNKNOWN
  })

export const getAdviserStatusText = (adviserStatus: ComputedRef<AdviserStatus>) => {
  const { t } = useI18n()
  switch (adviserStatus.value) {
    case AdviserStatus.AVAILABLE:
      return t('pages.advisor.adviser_banner.available')
    case AdviserStatus.BACK_AT_TIME:
      return t('pages.advisor.adviser_banner.back_at_time')
    case AdviserStatus.UNAVAILABLE:
      return t('pages.advisor.adviser_banner.unavailable')
    default:
      return t('pages.advisor.adviser_banner.unknown')
  }
}

export const showAndHideError = (message: string | null = null) => {
  const errorStore = useErrorStore()

  !message ? errorStore.showError() : errorStore.showSpecificError(message as string)
  setTimeout(() => {
    errorStore.hideError()
  }, BANNER_DURATION)
}
export const showAndHideSuccess = (message: string) => {
  const successStore = useSuccessStore()

  successStore.showSuccessMessage(message)
  setTimeout(() => {
    successStore.hideSuccess()
  }, BANNER_DURATION)
}

export const isDocumentAvailable = (document: Document) => {
  return (
    document.status === DocumentStatuses.Processed ||
    document.status === DocumentStatuses.Terminated ||
    document.status === DocumentStatuses.Completed ||
    document.status === DocumentStatuses.Accepted ||
    document.status === DocumentStatuses.Sent ||
    document.status === DocumentStatuses.RequestSubmitted
  )
}

export const isValidRedirectionUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    const allowedUrl = new URL(import.meta.env.VITE_RAILS_BACKEND_URL)
    return parsedUrl.hostname === allowedUrl.hostname && parsedUrl.protocol === allowedUrl.protocol
  } catch (e) {
    return false
  }
}

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const carB2bLinkParams = (meData: Me, isClient: boolean) => {
  return `&email=${meData.email}&phone_number=${meData.mobilePhone}&first_name=${meData.firstName}&last_name=${
    meData.lastName
  }&birthdate=${meData.birthdate?.split('T')[0]}&addressPostalCode=${meData.address?.zipCode}&addressStreet=${meData
    .address?.street}&addressNumber=${meData.address?.streetNumber}&addressCity=${meData.address?.city}&is_client=${
    meData.anyContract
  }&portfolio_id=${meData.portfolioId}&usage=professional&is_client=${isClient}`
}
